import { formatStatus } from "@/features/person/utils/utils";
import { isBeforeToday, isInFuture, toFormat } from "@/lib/dateHelpers";
import { getSectionName } from "@/pages/people/_utils";
import type { Band } from "@/types";
import { ProgramTypeName } from "@/types";

export const formatMembershipHistoryTableData = (bands: Band[]) =>
  bands.map((band) => ({
    id: band.id,
    section: getSectionName(band),
    isPrimarySectionAffiliation: band?.sectionAffiliation?.primary,
    program: band?.programType?.name,
    classification: band?.classification?.name,
    startDate: toFormat(band.startedOn),
    endDate: toFormat(band.expiresOn),
    startDateRaw: band.startedOn,
    endDateRaw: band.expiresOn,
    terminatesOn: band.terminatesOn,
    terminationDate: band.terminatesOn ? toFormat(band.terminatesOn) : null,
    startReason: band.createdReason ? formatStatus(band.createdReason) : null,
    terminationReason: band.terminatedReason ? formatStatus(band.terminatedReason) : null,
    notes: band.notes || []
  }));

export type MembershipHistoryRow = ReturnType<typeof formatMembershipHistoryTableData>[0];

export const isMembershipBandEnded = (row: MembershipHistoryRow): boolean => {
  return isBeforeToday(row.terminatesOn) || isBeforeToday(row.endDateRaw);
};

/** Mirrors Membership::Band#active? — started, not expired, not terminated before today. */
export const isMembershipBandActive = (band: Pick<Band, "startedOn" | "expiresOn" | "terminatesOn">): boolean => {
  if (!band.startedOn || !band.expiresOn) return false;
  if (isInFuture(band.startedOn)) return false;
  if (isBeforeToday(band.expiresOn)) return false;
  if (band.terminatesOn && isBeforeToday(band.terminatesOn)) return false;
  return true;
};

/**
 * Checkmark only when there are exactly two active bands.
 * Student + Associate → Associate; otherwise the active band on the primary section.
 */
export const getCheckedMembershipBandId = (bands: Band[]): string | null => {
  const activeBands = bands.filter(isMembershipBandActive);
  if (activeBands.length !== 2) return null;

  const studentBand = activeBands.find((band) => band.programType?.name === ProgramTypeName.Student);
  const associateBand = activeBands.find((band) => band.programType?.name === ProgramTypeName.Associate);

  if (studentBand && associateBand) {
    return associateBand.id;
  }

  return activeBands.find((band) => band.sectionAffiliation?.primary)?.id ?? null;
};

/** Tooltip for the dual-active section checkmark — Primary when that affiliation is primary, else Associate. */
export const getCheckedMembershipBandTooltip = (
  row: Pick<MembershipHistoryRow, "isPrimarySectionAffiliation">
): string => (row.isPrimarySectionAffiliation ? "Primary" : "Associate");
